import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Heart, Users, BookOpen } from 'lucide-react';
import { IMAGES, MOCK_EVENTS } from '../mock/mock';
import Reveal from '../components/Reveal';
import ContactSection from '../components/ContactSection';

const formatDate = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  return { month: d.toLocaleString('en-US', { month: 'short' }), day: d.getDate() };
};

const Home = () => {
  const events = MOCK_EVENTS.slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroCity} alt="Knoxville skyline" className="w-full h-full object-cover animate-kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0b2027]/70 via-[#0b2027]/45 to-[#0b2027]/85" />
        </div>
        <div className="relative text-center px-6">
          <Reveal>
            <p className="text-[#c9a24b] uppercase tracking-[0.4em] text-xs md:text-sm mb-6">Welcome to Living Hope</p>
          </Reveal>
          <Reveal delay={150}>
            <h1 className="font-serif-display italic text-white text-5xl md:text-7xl lg:text-8xl leading-tight">
              There is Hope.
            </h1>
            <h1 className="font-serif-display italic text-white text-5xl md:text-7xl lg:text-8xl leading-tight mt-2">
              Jesus.
            </h1>
          </Reveal>
          <Reveal delay={320}>
            <Link to="/plan-your-visit" className="btn-shine inline-flex items-center gap-3 mt-12 border border-[#c9a24b] text-[#c9a24b] hover:bg-[#c9a24b] hover:text-[#0b2027] px-10 py-4 uppercase tracking-[0.22em] text-sm font-600 transition-all duration-300">
              Plan Your Visit <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60">
          <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
          <div className="h-12 w-px bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>

      {/* WELCOME HOME */}
      <section className="bg-[#f6f2ea] py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="relative">
            <div className="relative overflow-hidden">
              <img src={IMAGES.congregation} alt="Worship" className="w-full h-[520px] object-cover hover:scale-105 transition-transform duration-1000" />
            </div>
            <div className="absolute -bottom-8 -right-4 md:-right-8 bg-[#0f2e38] p-8 max-w-[240px] shadow-2xl">
              <p className="font-serif-display italic text-[#c9a24b] text-3xl">Hope</p>
              <p className="text-white/80 text-sm mt-2">is for everyone and is found in Jesus.</p>
            </div>
          </Reveal>
          <Reveal delay={140}>
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-5">We're glad you're here</p>
            <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-6xl mb-8">Welcome Home</h2>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-6">
              We are a family of believers that values relationship with God and relationship with others. We come from different walks of life, but all connect in knowing we have a living hope through Jesus Christ.
            </p>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-10">
              Our desire is to see people be loved and given hope, no matter their situation.
            </p>
            <Link to="/about" className="group inline-flex items-center gap-3 text-[#17565f] uppercase tracking-[0.2em] text-sm font-600">
              Learn More
              <span className="h-px w-10 bg-[#17565f] group-hover:w-16 transition-all duration-300" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* VALUES STRIP */}
      <section className="bg-[#0f2e38] py-20">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid md:grid-cols-3 gap-10">
          {[
            { icon: Heart, title: 'Loved by God', text: 'Every person matters to God, no matter their story or season of life.' },
            { icon: Users, title: 'Better Together', text: 'We worship, laugh, eat, and grow together as one family of believers.' },
            { icon: BookOpen, title: 'Rooted in Truth', text: 'We are grounded in God’s Word and led by His Spirit in everything.' },
          ].map((v, i) => (
            <Reveal key={v.title} delay={i * 120} className="text-center">
              <div className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-[#c9a24b]/50 mb-6">
                <v.icon className="h-7 w-7 text-[#c9a24b]" />
              </div>
              <h3 className="font-serif-display text-white text-2xl mb-3">{v.title}</h3>
              <p className="text-white/65 leading-relaxed max-w-xs mx-auto">{v.text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FIND CONNECTION */}
      <section className="bg-[#f6f2ea] py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal className="order-2 lg:order-1">
            <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-5">Do life together</p>
            <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-6xl mb-8">Find Connection</h2>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-6">
              We believe connection and relationship with others is vital in the life of the believer. We worship together, we laugh together, we eat together, and we grow together.
            </p>
            <p className="text-[#3a4d54] text-lg leading-relaxed mb-10">
              We offer small groups, times of fellowship, and ways to serve as a way of connection at Living Hope.
            </p>
            <Link to="/connect" className="group inline-flex items-center gap-3 text-[#17565f] uppercase tracking-[0.2em] text-sm font-600">
              Learn More
              <span className="h-px w-10 bg-[#17565f] group-hover:w-16 transition-all duration-300" />
            </Link>
          </Reveal>
          <Reveal delay={140} className="order-1 lg:order-2">
            <div className="grid grid-cols-2 gap-4">
              <img src={IMAGES.fellowship} alt="Fellowship" className="w-full h-64 object-cover mt-8" />
              <img src={IMAGES.groupIndoor} alt="Community" className="w-full h-64 object-cover" />
              <img src={IMAGES.outdoorGroup} alt="Group" className="w-full h-64 object-cover col-span-2" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* UPCOMING EVENTS */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="text-[#c9a24b] uppercase tracking-[0.34em] text-xs mb-4">What's happening</p>
              <h2 className="font-serif-display text-[#0f2e38] text-4xl md:text-6xl">Upcoming Events</h2>
            </div>
            <Link to="/events" className="inline-flex items-center gap-2 text-[#17565f] uppercase tracking-[0.2em] text-sm font-600 hover:gap-4 transition-all">
              View Calendar <ArrowUpRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-8">
            {events.map((ev, i) => {
              const d = formatDate(ev.date);
              return (
                <Reveal key={ev.id} delay={i * 120}>
                  <Link to="/events" className="group block">
                    <div className="relative overflow-hidden">
                      <img src={ev.image} alt={ev.title} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-700" />
                      <div className="absolute top-4 left-4 bg-[#0f2e38] text-white text-center px-4 py-2">
                        <p className="text-[#c9a24b] text-xs uppercase tracking-widest">{d.month}</p>
                        <p className="font-serif-display text-2xl leading-none">{d.day}</p>
                      </div>
                    </div>
                    <div className="pt-6">
                      <span className="text-xs uppercase tracking-[0.2em] text-[#2c8a95]">{ev.category}</span>
                      <h3 className="font-serif-display text-2xl text-[#0f2e38] mt-2 group-hover:text-[#17565f] transition-colors">{ev.title}</h3>
                      <p className="text-[#5a6b71] mt-3 line-clamp-2">{ev.description}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <ContactSection />
    </div>
  );
};

export default Home;
